import { useSyncExternalStore } from 'react';
import { Customer, PaginatedResponse } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';

export interface CustomerListParams {
  page: number;
  pageSize: number;
  q?: string;
  sortBy?: 'registeredAt' | 'name';
  sortDir?: 'asc' | 'desc';
}

const buildQuery = (params: CustomerListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  q: params.q || undefined,
  sortBy: params.sortBy || undefined,
  sortDir: params.sortDir || undefined
});

const normalizeItem = (item: Customer): Customer => ({
  ...item,
  logs: item.logs || [],
  changeLogs: item.changeLogs || []
});

export const useCustomers = (params: CustomerListParams): PaginatedResponse<Customer> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('customers', cb),
    () => 0,
    () => 0
  );
  const result = ctx.read('customers', params, async (p: CustomerListParams) => {
    const data = await api.listStaffCustomers(buildQuery(p));
    return { ...data, items: (data.items || []).map(normalizeItem) };
  });
  return result;
};

export interface CustomerMutations {
  update: (customer: Customer) => Promise<void>;
  remove: (customerId: string) => Promise<void>;
  resetPassword: (customerEmail: string) => Promise<void>;
}

export const useCustomerMutations = (): CustomerMutations => {
  const ctx = useStaffDataContext();
  const after = () => ctx.invalidate('customers');
  return {
    update: async (customer) => {
      await api.updateCustomer(customer);
      after();
    },
    remove: async (customerId) => {
      await api.deleteCustomer(customerId);
      after();
    },
    resetPassword: async (customerEmail) => {
      await api.requestPasswordReset(customerEmail);
    }
  };
};
